import {
  BankOutlined,
  CheckCircleFilled,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Empty, Input, Spin, Tag, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import {
  getSelectedTenant,
  getTenantMemberships,
  switchActiveOrganization,
} from 'services/tenant/tenantMembershipService';
import { hasOrganizationPermission, ORGANIZATION_PERMISSIONS } from 'services/tenant/organizationPermissions';

const STATUS_LABELS = {
  active: 'Active',
  pending: 'Pending',
  'temporary-access': 'Temporary Access',
  provisioning: 'Provisioning',
  expired: 'Expired',
  inactive: 'Inactive',
  suspended: 'Suspended',
};

const isUnavailable = (tenant) =>
  ['inactive', 'suspended'].includes(tenant.status);

const humanizeStatus = (value, fallback = 'Not available') => value
  ? String(value).toLowerCase().split(/[_-]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
  : fallback;

export default function TenantSwitcher() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [switchingId, setSwitchingId] = useState(null);
  const [query, setQuery] = useState('');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(getSelectedTenant);
  const navigate = useNavigate();

  useEffect(() => {
    setLoadError(null);
    getTenantMemberships()
      .then((memberships) => {
        setTenants(memberships);
        const selected = getSelectedTenant();
        setSelectedTenant(memberships.find((tenant) => tenant.id === selected?.id) || null);
      })
      .catch((error) => setLoadError(error?.message || 'Unable to load organizations.'))
      .finally(() => setLoading(false));
  }, []);

  const filteredTenants = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tenants;

    return tenants.filter((tenant) =>
      [tenant.name, tenant.identifier, tenant.plan, tenant.role, tenant.subscriptionStatus, tenant.paymentStatus, tenant.accessStatus]
        .some((value) => String(value || '').toLowerCase().includes(normalizedQuery))
    );
  }, [query, tenants]);

  const switchTenant = (tenant) => {
    if (isUnavailable(tenant) || switchingId) return;

    if (tenant.id === selectedTenant?.id) {
      setOpen(false);
      window.location.assign(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`);
      return;
    }

    try {
      setSwitchingId(tenant.id);
      const selected = switchActiveOrganization(tenant.id);
      setSelectedTenant(selected);
      setOpen(false);
      window.location.assign(`${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`);
    } catch (error) {
      setSwitchingId(null);
      message.error(error?.message || 'Unable to switch organization.');
    }
  };

  const openTenantSelection = () => {
    setOpen(false);
    navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.tenantSelection}?mode=manage`);
  };

  const dropdownContent = (
    <div className="tenant-switcher-dropdown">
      <Input
        prefix={<SearchOutlined />}
        placeholder="Search organization..."
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onClick={(event) => event.stopPropagation()}
        allowClear
      />

      <div className="tenant-switcher-list">
        {loading ? (
          <div className="tenant-switcher-loading"><Spin size="small" /></div>
        ) : loadError ? (
          <div className="tenant-switcher-feedback tenant-switcher-feedback--error">{loadError}</div>
        ) : !filteredTenants.length ? (
          <div className="tenant-switcher-feedback"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No organizations found" /></div>
        ) : filteredTenants.map((tenant) => {
          const selected = tenant.id === selectedTenant?.id;
          const unavailable = isUnavailable(tenant);
          const canViewBilling = hasOrganizationPermission(tenant, ORGANIZATION_PERMISSIONS.BILLING_VIEW);

          return (
            <button
              type="button"
              className={`tenant-switcher-option${selected ? ' tenant-switcher-option--selected' : ''}`}
              key={tenant.id}
              disabled={unavailable || Boolean(switchingId)}
              onClick={() => switchTenant(tenant)}
            >
              <span className="tenant-switcher-option-icon"><BankOutlined /></span>
              <span className="tenant-switcher-option-content">
                <strong>{tenant.name}</strong>
                <small>{tenant.identifier}</small>
                <span className="tenant-switcher-membership-role">Your Role: <b>{tenant.role}</b></span>
                <dl className="tenant-switcher-organization-facts">
                  <div><dt>Plan</dt><dd>{tenant.plan || 'Not selected'}</dd></div>
                  <div><dt>Subscription</dt><dd>{canViewBilling ? humanizeStatus(tenant.subscriptionStatus, 'Not started') : 'Restricted'}</dd></div>
                  <div><dt>Billing</dt><dd>{canViewBilling ? humanizeStatus(tenant.paymentStatus, 'Not started') : 'Restricted'}</dd></div>
                  <div><dt>Access</dt><dd>{humanizeStatus(tenant.accessStatus, 'Exploration')}</dd></div>
                </dl>
              </span>
              <span className="tenant-switcher-option-status">
                <Tag className={`tenant-switcher-status tenant-switcher-status--${tenant.status}`}>
                  {STATUS_LABELS[tenant.status]}
                </Tag>
                {switchingId === tenant.id ? <Spin size="small" /> : selected && <CheckCircleFilled />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="tenant-switcher-footer">
        <Button type="text" block icon={<PlusOutlined />} onClick={openTenantSelection}>
          Request Access to Organization
        </Button>
        <Button type="text" block icon={<SettingOutlined />} onClick={openTenantSelection}>
          Manage Organizations
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomLeft"
      dropdownRender={() => dropdownContent}
    >
      <Button className="tenant-switcher-trigger">
        <BankOutlined />
        <span>{selectedTenant?.name || 'Select Organization'}</span>
        <DownOutlined />
      </Button>
    </Dropdown>
  );
}
